import type { JSX } from "preact";
import { useEffect, useState } from "preact/compat";
import { objectKeys } from "@gss/common/objectUtil";
import { Button } from "@gss/components/Button";
import { Edit, Save } from "@gss/components/icons";
import { InputGroup } from "@gss/components/InputGroup";
import { InputText } from "@gss/components/InputText";
import { TextEditor } from "@gss/editor/TextEditor";
import { isNotEmpty } from "@gss/editor/util";
import type { NewsPostNew } from "./news.types";

interface PostType {
    label: string;
    title: string;
    value: string;
}

export const POST_TYPES = {
    NEWS: {
        label: "Nyhet",
        title: "Skriv rubrik här",
        value: "Skriv nyhet här",
    },
    FAQ: {
        label: "FAQ",
        title: "Skriv fråga här",
        value: "Skriv svar här",
    },
} as const satisfies Record<string, PostType>;

interface Parameters {
    post: NewsPostNew;
    inEditMode: boolean;
    allowEdit: boolean;
    type?: PostType;
    editPost: (post: NewsPostNew) => void;
    savePost: (post: NewsPostNew) => void;
}

interface Validation {
    title: boolean;
    value: boolean;
}

export function Post({
    post: inputPost,
    inEditMode,
    allowEdit,
    type = POST_TYPES.FAQ,
    editPost,
    savePost,
}: Parameters): JSX.Element | null {
    const [post, setPost] = useState<NewsPostNew>();
    const [validation, setValidation] = useState<Validation>();

    useEffect(() => {
        setPost(inputPost);
        const valid = inputPost.id != null;
        setValidation({
            title: valid,
            value: valid,
        });
    }, [inputPost]);

    if (post == null || validation == null) {
        return null;
    }

    if (inEditMode) {
        return (
            <div className="news-post">
                <InputGroup className="mb-3">
                    <InputText
                        placeholder={type.title}
                        value={post.title}
                        invalid={!validation.title}
                        onChange={(value) => {
                            setPost({ ...post, title: value });
                            setValidation({
                                ...validation,
                                title: value.trim() !== "",
                            });
                        }}
                    />
                    <Button
                        variant="outline-secondary"
                        disabled={objectKeys(validation).some(
                            (k) => !validation[k],
                        )}
                        onClick={() => savePost(post)}
                    >
                        <Save className="align-baseline text-success" />
                    </Button>
                </InputGroup>

                <TextEditor
                    value={post.value}
                    placeholder={type.value}
                    isInvalid={!validation.value}
                    onChange={(value) => {
                        setPost({ ...post, value });
                        setValidation({
                            ...validation,
                            value: isNotEmpty(value),
                        });
                    }}
                />
            </div>
        );
    }

    const date =
        post.created != null
            ? new Date(post.created).toLocaleDateString("sv")
            : null;

    return (
        <div className="news-post">
            <h2 className="h3 mb-0">
                {post.title}
                {allowEdit && (
                    <Button
                        variant={null}
                        className="p-0 m-0 float-end"
                        onClick={() => editPost(post)}
                    >
                        <Edit className="align-baseline" />
                    </Button>
                )}
            </h2>
            <div className="text-muted">{date}</div>
            <TextEditor readOnly value={post.value} />
        </div>
    );
}
